import { IconDeviceMobile } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { isApiError, listenLive, QrCode } from '@tnn/app-kit'
import { formatDate, formatTime } from '@tnn/core'
import { bangkokTime, type ClockedHere, type ClockScreenState } from '@tnn/domain'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classes from './clock.module.css'
import { Frame, useNow } from './frame'
import screenClasses from './screen.module.css'

export const clockKey = ['screen', 'clock'] as const
export const clockUnpaired = (error: unknown) => isApiError(error) && String(error.code) === 'CLOCK_SCREEN_UNPAIRED'

/** How long who just clocked stays under the QR */
const SHOWN_MS = 4000

/** Who just clocked here, a moment each: not whoever the screen found as it opened (they clocked before) */
function useJustClocked(last: ClockedHere | null) {
  const latest = useRef(last)
  latest.current = last
  // undefined until the first state is seen
  const seen = useRef<string | null | undefined>(undefined)
  const [shown, setShown] = useState<ClockedHere | null>(null)
  const id = last?.id ?? null
  useEffect(() => {
    if (seen.current === undefined) {
      seen.current = id
      return
    }
    if (!id || id === seen.current) return
    seen.current = id
    setShown(latest.current)
  }, [id])
  useEffect(() => {
    if (!shown) return
    const gone = window.setTimeout(() => setShown(null), SHOWN_MS)
    return () => window.clearTimeout(gone)
  }, [shown])
  return shown
}

/**
 * A branch's clock screen (hr.clockDisplay), a phone or tablet at the counter: the branch, the time, and the time
 * clock's QR, good once — the server tells the moment someone opens it (`/api/screen/clock/events`), and the next one
 * shows at once, every 30 seconds anyway. Who just clocked shows a moment under it. No unpairing here: HR or the lead
 * pairs another device or revokes it, and it goes back to pairing
 */
export function ClockScreen({ state, company }: { state: ClockScreenState; company?: string }) {
  const { t } = useTranslation()
  const client = useQueryClient()
  const now = useNow()
  const just = useJustClocked(state.last)

  useEffect(() => listenLive('/screen/clock/events', () => client.invalidateQueries({ queryKey: clockKey })), [client])

  return (
    <Frame
      company={company}
      calm
      foot={
        <span className={screenClasses.owner}>
          <IconDeviceMobile size={18} stroke={1.75} aria-hidden />
          {state.name}
        </span>
      }
    >
      <section className={classes.clock}>
        <p className={classes.branch}>{state.branchName}</p>
        <time className={classes.time} dateTime={now.toISOString()}>
          {formatTime(now)}
        </time>
        <p className={classes.day}>{formatDate(now, { style: 'day' })}</p>
        {/* A new code is a new card: it settles in, so the change is seen */}
        <QrCode key={state.code} className={classes.qr} value={state.url} size={320} label={t('screen.clockQr')} />
        <p className={classes.hint}>{t('screen.clockHint')}</p>
        <p className={classes.once}>{t('screen.clockOnce')}</p>
      </section>
      {just && (
        <div className={classes.clocked} role="status" key={just.id}>
          <span className={classes.tick} aria-hidden>
            <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden>
              <path d="M13 25.5 20.5 33 35 17" />
            </svg>
          </span>
          <span>{t(just.kind === 'in' ? 'screen.clockedIn' : 'screen.clockedOut', { name: just.name, time: bangkokTime(just.at) })}</span>
        </div>
      )}
    </Frame>
  )
}
